import {Platform} from 'react-native';
import Config from 'react-native-config';
import deviceInfoModule from 'react-native-device-info';

import {manualLog, requestLog} from './splunk';

class _Logger {
  private getBrowserName = () => {
    if (Platform.OS !== 'web' || typeof navigator === 'undefined') {
      return undefined;
    }

    const ua = navigator.userAgent || '';
    if (ua.includes('Edg/')) return 'Edge';
    if (ua.includes('Chrome/') && !ua.includes('Edg/')) return 'Chrome';
    if (ua.includes('Safari/') && !ua.includes('Chrome/')) return 'Safari';
    if (ua.includes('Firefox/')) return 'Firefox';
    return 'Unknown';
  };

  private getBrowserVersion = () => {
    if (Platform.OS !== 'web' || typeof navigator === 'undefined') {
      return undefined;
    }

    const ua = navigator.userAgent || '';
    const matchers = [
      /Edg\/([\d.]+)/,
      /Chrome\/([\d.]+)/,
      /Version\/([\d.]+).*Safari/,
      /Firefox\/([\d.]+)/,
    ];

    for (const matcher of matchers) {
      const match = ua.match(matcher);
      if (match?.[1]) {
        return match[1];
      }
    }

    return 'unknown';
  };

  private getHostOSInfo = () => {
    if (Platform.OS !== 'web' || typeof navigator === 'undefined') {
      return {hostOS: undefined, hostOSVersion: undefined};
    }

    const ua = navigator.userAgent || '';

    // iOS user agents include "like Mac OS X", so detect iOS before macOS.
    const iosMatch = ua.match(/(?:iPhone|iPad|iPod).*OS (\d+[_\d]*)/);
    if (iosMatch?.[1]) {
      return {hostOS: 'iOS', hostOSVersion: iosMatch[1].replace(/_/g, '.')};
    }

    const windowsMatch = ua.match(/Windows NT ([\d.]+)/);
    if (windowsMatch?.[1]) {
      return {hostOS: 'Windows', hostOSVersion: windowsMatch[1]};
    }

    const macMatch = ua.match(/Macintosh;.*Mac OS X (\d+[_\d]*)/);
    if (macMatch?.[1]) {
      return {hostOS: 'macOS', hostOSVersion: macMatch[1].replace(/_/g, '.')};
    }

    const androidMatch = ua.match(/Android ([\d.]+)/);
    if (androidMatch?.[1]) {
      return {hostOS: 'Android', hostOSVersion: androidMatch[1]};
    }

    return {hostOS: 'Unknown', hostOSVersion: 'unknown'};
  };

  private getWebDeviceId = () => {
    const fallbackId = 'web-anonymous';

    try {
      if (typeof window === 'undefined') {
        return fallbackId;
      }

      const storageKey = 'connect_web_device_id';
      const existingId = window.localStorage.getItem(storageKey);
      if (existingId) {
        return existingId;
      }

      const generatedId =
        typeof window.crypto?.randomUUID === 'function'
          ? window.crypto.randomUUID()
          : `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

      window.localStorage.setItem(storageKey, generatedId);
      return generatedId;
    } catch {
      return fallbackId;
    }
  };

  private hasWebProxy = () =>
    Platform.OS === 'web' && Boolean(Config?.SPLUNK_WEB_PROXY_URL);

  private hasAuthorizationKey = () => Boolean(Config?.SPLUNK_KEY);

  private shouldSendLogs = () =>
    this.hasWebProxy() || this.hasAuthorizationKey();

  private getDeviceInfo = () => {
    let version = Config?.APP_VERSION || Config?.VERSION || 'web';
    let uniqueId = Platform.OS === 'web' ? this.getWebDeviceId() : 'unknown';

    if (Platform.OS !== 'web') {
      try {
        version = deviceInfoModule.getVersion() || version;
      } catch {}

      try {
        if (typeof deviceInfoModule.getUniqueIdSync === 'function') {
          uniqueId = deviceInfoModule.getUniqueIdSync() || uniqueId;
        }
      } catch {}
    }

    const {hostOS, hostOSVersion} = this.getHostOSInfo();

    return {
      OS: Platform.OS,
      version,
      uniqueId,
      browser: this.getBrowserName(),
      browserVersion: this.getBrowserVersion(),
      hostOS,
      hostOSVersion,
    };
  };

  requestSplunkLog = (props: any) => {
    if (!this.shouldSendLogs()) {
      return;
    }

    requestLog({
      authorizationKey: Config.SPLUNK_KEY,
      deviceInfo: this.getDeviceInfo(),
      logType: props.logType,
      message: props.message,
      requestData: props.requestData,
      responseData: props.responseData,
      severity: props.severity,
      source: 'connect_app',
      sourceType: 'connect:app:events',
      requestId: props.requestId || '',
    }).catch(() => {});
  };

  manualSplunkLog = (props: any) => {
    if (!this.shouldSendLogs()) {
      return;
    }

    manualLog({
      authorizationKey: Config.SPLUNK_KEY,
      deviceInfo: this.getDeviceInfo(),
      extraData: props.extraData,
      loginType: props.loginType,
      message: props.message,
      severity: props.severity,
      source: 'connect_app',
      sourceType: 'connect:app:events',
    }).catch(() => {});
  };
}

export const logger = new _Logger();
export const Logger = logger; // To save back compatibility
